/**
 * The admin screen — who is in the region, what role they hold, and which
 * screens a general user may view or edit.
 *
 * Two layers, and this screen is only the second. Entra decides whether
 * someone can sign in at all (the two Global Scout EmailConfig groups). Here:
 *
 *  - **Role.** `<region>_admin` sees and edits every screen in the region and
 *    manages its people. `<region>_user` sees nothing until given screens.
 *  - **Per-screen access, general users only.** View or edit, one screen at a
 *    time. Admins need none: their role already covers the region.
 *
 * Scoped to the active region. The API has already confined `people`, `roles`
 * and `areas` to the regions the caller manages, and enforces every rule
 * below again on save — this screen only avoids offering a change it would
 * refuse. Global admins are never editable here except by another global
 * admin, never removable here at all, and nobody can change themselves.
 */

import { useMemo, useState } from 'react'
import { ApiError } from '../api'
import {
  useDeletePermission,
  usePermissions,
  useSavePermission,
  useSchema,
} from '../api/hooks'
import type { AccessChange, AccessPerson } from '../api/types'
import { formatTimestampShort, shortenUpn } from '../lib/format'
import { Banner, EmptyState, ErrorBanner, Spinner } from '../components/feedback'
import { ConfirmDialog } from '../components/Drawer'
import { useToasts } from '../components/toast-context'
import { useRegion } from '../region'

type Level = 'none' | 'view' | 'edit'

function levelOf(person: AccessPerson, area: string): Level {
  const grant = person.grants[area]
  if (!grant) return 'none'
  return grant.can_write ? 'edit' : 'view'
}

const nameOf = (person: AccessPerson) => person.display_name ?? person.user_upn

export function PermissionsPage() {
  const schemaQuery = useSchema()
  const access = usePermissions()
  const save = useSavePermission()
  const remove = useDeletePermission()
  const toasts = useToasts()
  const { region } = useRegion()
  const [removing, setRemoving] = useState<AccessPerson | null>(null)

  const me = schemaQuery.data?.user
  const regionLabel = schemaQuery.data?.regions.find((r) => r.key === region)?.label ?? region

  const areas = useMemo(() => access.data?.areas[region] ?? [], [access.data, region])
  const roles = useMemo(
    () => (access.data?.roles ?? []).filter((r) => r.region === region),
    [access.data, region],
  )
  // People waiting on an admin first, then by name.
  const people = useMemo(
    () =>
      (access.data?.people ?? [])
        .filter((p) => p.region === region)
        .sort((a, b) => Number(b.pending) - Number(a.pending) || nameOf(a).localeCompare(nameOf(b))),
    [access.data, region],
  )
  const pendingCount = people.filter((p) => p.pending).length

  const canEdit = (person: AccessPerson) =>
    person.user_oid !== me?.oid && (!person.is_global_admin || Boolean(me?.is_global_admin))
  const canRemove = (person: AccessPerson) =>
    person.user_oid !== me?.oid && !person.is_global_admin

  const apply = async (person: AccessPerson, change: AccessChange, done?: string) => {
    try {
      await save.mutateAsync({ userOid: person.user_oid, change })
      if (done) toasts.success(done, nameOf(person))
    } catch (error) {
      toasts.error(
        'Could not save that change',
        error instanceof ApiError ? (error.detail ?? error.message) : 'Unexpected error',
      )
    }
  }

  const setLevel = (person: AccessPerson, area: string, level: Level) => {
    const grants = { ...person.grants }
    if (level === 'none') delete grants[area]
    else grants[area] = { can_write: level === 'edit' }
    void apply(person, { grants })
  }

  const setRole = (person: AccessPerson, roleKey: string) => {
    const role = roles.find((r) => r.role_key === roleKey)
    if (!role) return
    // Admins hold no per-screen access, and someone stepping down from admin
    // starts from nothing rather than inheriting a set nobody chose.
    const grants = role.is_admin || person.is_admin ? {} : person.grants
    void apply(
      person,
      { role_key: roleKey, grants },
      role.is_admin
        ? `Now ${role.label} — full access to ${regionLabel}`
        : `Now ${role.label}${person.is_admin ? ' — no screens yet' : ''}`,
    )
  }

  const confirmRemove = async () => {
    if (!removing) return
    try {
      await remove.mutateAsync(removing.user_oid)
      toasts.success('Removed', nameOf(removing))
    } catch (error) {
      toasts.error(
        'Could not remove that person',
        error instanceof ApiError ? (error.detail ?? error.message) : 'Unexpected error',
      )
    }
    setRemoving(null)
  }

  if (schemaQuery.isLoading || access.isLoading) {
    return <div className="page"><Spinner label="Loading access…" /></div>
  }

  if (me && !me.can_manage_access) {
    return (
      <div className="page">
        <Banner tone="warn" title="Only admins manage access">
          Ask an admin for your region if you need access to more screens.
        </Banner>
      </div>
    )
  }

  if (access.data && !(region in access.data.areas)) {
    return (
      <div className="page">
        <Banner tone="info" title={`You do not manage ${regionLabel}`}>
          Switch region in the sidebar to the region you administer.
        </Banner>
      </div>
    )
  }

  return (
    <div className="page">
      <header className="page__header">
        <div className="page__title-row">
          <div>
            <h1 className="page__title">Access — {regionLabel}</h1>
            <p className="page__description">
              People appear here after their first sign-in, once they pick {regionLabel}.
              They start with nothing: make them an admin, or give a general user the
              screens they need. Every change is checked again by the server and
              recorded in Recent changes.
            </p>
          </div>
        </div>
      </header>

      <ErrorBanner error={access.error} context="Could not load the access list." />

      {pendingCount > 0 ? (
        <Banner tone="warn" title={`${pendingCount} waiting for access`}>
          They signed in and picked {regionLabel}, and cannot see anything yet. They are
          listed first.
        </Banner>
      ) : null}

      <div className="card">
        {people.length === 0 ? (
          <EmptyState title={`Nobody in ${regionLabel} yet`}>
            People appear here after they sign in and pick {regionLabel}. Signing in needs
            membership of a Global Scout EmailConfig group, managed by its owners in Entra.
          </EmptyState>
        ) : (
          <div className="table-wrap">
            <table className="table perm-table">
              <thead>
                <tr>
                  <th scope="col">Person</th>
                  <th scope="col">Role</th>
                  {areas.map((a) => (
                    <th key={a.area} scope="col" className="perm-table__type">
                      {a.label}
                    </th>
                  ))}
                  <th scope="col">Added</th>
                  <th scope="col">
                    <span className="visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {people.map((person) => {
                  const editable = canEdit(person)
                  const busy = save.isPending || remove.isPending
                  const isMe = person.user_oid === me?.oid
                  return (
                    <tr key={person.user_oid} style={{ cursor: 'default' }}>
                      <td>
                        <div>
                          {nameOf(person)}
                          {isMe ? <span className="badge badge--off perm-badge">you</span> : null}
                          {person.pending ? (
                            <span className="badge badge--warn perm-badge">waiting</span>
                          ) : null}
                        </div>
                        <div className="table__cell--muted">{person.user_upn}</div>
                      </td>
                      <td>
                        {person.is_global_admin ? (
                          <span className="badge badge--info">Global admin</span>
                        ) : (
                          <select
                            className="select select--filter"
                            value={person.role_key}
                            disabled={!editable || busy}
                            onChange={(event) => setRole(person, event.target.value)}
                            aria-label={`Role for ${nameOf(person)}`}
                            title={isMe ? 'You cannot change your own access' : undefined}
                          >
                            {roles.map((r) => (
                              <option key={r.role_key} value={r.role_key}>
                                {r.label}
                              </option>
                            ))}
                          </select>
                        )}
                      </td>
                      {person.is_admin || person.is_global_admin ? (
                        areas.length > 0 ? (
                          <td colSpan={areas.length} className="perm-cell perm-cell--all">
                            Full access to every screen
                          </td>
                        ) : null
                      ) : (
                        areas.map((a) => (
                          <td key={a.area} className="perm-cell">
                            <select
                              className={`select select--filter perm-level perm-level--${levelOf(person, a.area)}`}
                              value={levelOf(person, a.area)}
                              disabled={!editable || busy}
                              onChange={(event) => setLevel(person, a.area, event.target.value as Level)}
                              aria-label={`${nameOf(person)}: ${a.label}`}
                            >
                              <option value="none">No access</option>
                              <option value="view">View</option>
                              <option value="edit">Edit</option>
                            </select>
                          </td>
                        ))
                      )}
                      <td className="table__cell--muted">
                        {formatTimestampShort(person.added_at)}
                        <div>by {shortenUpn(person.added_by)}</div>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          onClick={() => setRemoving(person)}
                          disabled={!canRemove(person) || busy}
                          title={
                            isMe
                              ? 'You cannot remove yourself'
                              : person.is_global_admin
                                ? 'Global admins are managed in the database'
                                : undefined
                          }
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {removing ? (
        <ConfirmDialog
          title={`Remove ${nameOf(removing)}?`}
          confirmLabel="Remove"
          busy={remove.isPending}
          onConfirm={confirmRemove}
          onCancel={() => setRemoving(null)}
          body={
            <p>
              Their role and every screen they were given are deleted. They can still sign
              in, and will be asked to pick a region again, starting with no access. To stop
              them signing in at all, remove them from the Entra group. The change is
              recorded in Recent changes.
            </p>
          }
        />
      ) : null}
    </div>
  )
}
